import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Printer, Mail } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  getTailoringOrderInvoices,
  getTailoringOrder,
  getTailoringPayments,
  updateTailoringOrderStatus,
  updateTailoringOrder,
  cancelTailoringOrder,
  downloadTailoringReceipt,
  sendTailoringReceipt,
  checkCashRegister,
  updateSubOrderStatus,
  getCompanyConfig,
  updateTailoringSubOrders,
  getAllProducts,
} from "../lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "PENDIENTE_PAGO",    label: "Pendiente de pago" },
  { value: "PRE_PRODUCCION",    label: "Pre-producción" },
  { value: "EN_PRODUCCION",     label: "En producción" },
  { value: "CONFECCIONADO",     label: "Confeccionado" },
  { value: "LISTO_PARA_ENTREGA", label: "Listo para entrega" },
  { value: "ENTREGADO",         label: "Entregado" },
  { value: "CANCELADO",         label: "Cancelado" },
];

const STATUS_COLORS = {
  PENDIENTE_PAGO:     "bg-yellow-100 text-yellow-800",
  PRE_PRODUCCION:     "bg-blue-100 text-blue-800",
  EN_PRODUCCION:      "bg-indigo-100 text-indigo-800",
  CONFECCIONADO:      "bg-purple-100 text-purple-800",
  LISTO_PARA_ENTREGA: "bg-green-100 text-green-800",
  ENTREGADO:          "bg-slate-100 text-slate-700",
  CANCELADO:          "bg-red-100 text-red-700",
};

const money = (n) =>
  Number(n || 0).toLocaleString("es-DO", {
    style: "currency",
    currency: "DOP",
  });

const TailoringOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [editMeas, setEditMeas] = useState({});
  const [editChanges, setEditChanges] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const [order, setOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [orderInvoices, setOrderInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cajaOpen, setCajaOpen] = useState(true);
  const [cajaStale, setCajaStale] = useState(false);
  const [cajaStaleDate, setCajaStaleDate] = useState('');

  const [statusToSet, setStatusToSet] = useState("");
  const [sendEmailModal, setSendEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [askSaveEmail, setAskSaveEmail] = useState(null);
  const [printingReceipt, setPrintingReceipt] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [creatingNc, setCreatingNc] = useState(false);
  const [deliverWithDebtOpen, setDeliverWithDebtOpen] = useState(false);
  const [changingSubOrder, setChangingSubOrder] = useState(null);

  // Edición de prendas
  const [showEditPrendas, setShowEditPrendas] = useState(false);
  const [editPrendas, setEditPrendas] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [savingPrendas, setSavingPrendas] = useState(false);
  const MEDIDAS = ['pecho', 'cintura', 'cadera', 'largo', 'manga'];
  const emptyPrenda = { product_id: '', product_name: '', item_price: '', discount_pct: '', measurements: {}, changes_requested: '', work_notes: '', services: [] };
  const [cardSurchargePct, setCardSurchargePct] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [anticiPoAmount, setAnticiPoAmount] = useState('');

  const statusLabel = useMemo(() => {
    const m = new Map(STATUS_OPTIONS.map((x) => [x.value, x.label]));
    return (s) => m.get(s) || s;
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [o, p, inv] = await Promise.all([
        getTailoringOrder(id),
        getTailoringPayments(id),
        getTailoringOrderInvoices(id),
      ]);
      setOrder(o);
      setEditMeas(o?.customization?.measurements || {});
      setEditChanges(o?.customization?.changes_requested || "");
      setEditNotes(o?.notes || "");
      setPayments(p || []);
      setOrderInvoices(inv || []);
      setStatusToSet(o?.status || "");
      // Si no hay pagos aún, inicializar anticipo con el mínimo 50%
      const oTotal = Number(o?.pricing?.total_amount || 0);
      const oPaid  = Number(o?.pricing?.paid_amount  || 0);
      if (oPaid === 0 && oTotal > 0) {
        setAnticiPoAmount((Math.ceil(oTotal * 0.5 * 100) / 100).toFixed(2));
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "Error cargando pedido");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); /* eslint-disable-next-line */
    checkCashRegister().then(res => {
      setCajaOpen(!res.required || res.open);
      setCajaStale(res.stale || false);
      setCajaStaleDate(res.stale_date || '');
    }).catch(() => setCajaOpen(true));
    getCompanyConfig().then(cfg => setCardSurchargePct(parseFloat(cfg?.card_surcharge_pct || 0))).catch(() => {});
  }, [id]);

  const subOrders   = order?.sub_orders || [];
  const isNewFormat = subOrders.length > 0;

  const openEditPrendas = async () => {
    if (!allProducts.length) {
      try { const p = await getAllProducts(); setAllProducts(p || []); } catch {}
    }
    setEditPrendas(subOrders.map(so => ({
      id: so.id, product_id: so.product_id, product_name: so.product_name,
      item_price: String(so.item_price || ''), discount_pct: String(so.discount_pct || ''),
      measurements: { ...so.measurements }, changes_requested: so.changes_requested || '',
      work_notes: so.work_notes || '',
      services: (so.services || []).map(s => ({ ...s, unit_price: String(s.unit_price || ''), quantity: String(s.quantity || 1) })),
      status: so.status,
    })));
    setShowEditPrendas(true);
  };

  const updatePrenda = (idx, field, value) =>
    setEditPrendas(p => p.map((so, i) => i === idx ? { ...so, [field]: value } : so));

  const updatePrendaMedida = (idx, key, value) =>
    setEditPrendas(p => p.map((so, i) => i === idx ? { ...so, measurements: { ...so.measurements, [key]: value } } : so));

  const updatePrendaService = (pIdx, sIdx, field, value) =>
    setEditPrendas(p => p.map((so, i) => i !== pIdx ? so : {
      ...so, services: so.services.map((s, j) => j === sIdx ? { ...s, [field]: value } : s)
    }));

  const removePrendaService = (pIdx, sIdx) =>
    setEditPrendas(p => p.map((so, i) => i !== pIdx ? so : { ...so, services: so.services.filter((_, j) => j !== sIdx) }));

  const addPrendaService = (pIdx) =>
    setEditPrendas(p => p.map((so, i) => i !== pIdx ? so : {
      ...so, services: [...so.services, { product_id: '', name: '', quantity: '1', unit_price: '' }]
    }));

  const saveEditPrendas = async () => {
    for (let i = 0; i < editPrendas.length; i++) {
      if (!editPrendas[i].product_id) { toast.error(`Prenda ${i + 1}: selecciona un producto`); return; }
      if (!parseFloat(editPrendas[i].item_price)) { toast.error(`Prenda ${i + 1}: precio inválido`); return; }
    }
    setSavingPrendas(true);
    try {
      const payload = editPrendas.map(so => ({
        id: so.id,
        product_id: so.product_id,
        item_price: parseFloat(so.item_price) || 0,
        discount_pct: parseFloat(so.discount_pct) || 0,
        measurements: so.measurements,
        changes_requested: so.changes_requested,
        work_notes: so.work_notes,
        status: so.status,
        services: so.services.filter(s => s.product_id).map(s => ({
          product_id: s.product_id,
          quantity: parseFloat(s.quantity) || 1,
          unit_price: parseFloat(s.unit_price) || 0,
        })),
      }));
      const updated = await updateTailoringSubOrders(id, payload);
      setOrder(updated);
      setShowEditPrendas(false);
      toast.success('Prendas actualizadas');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error guardando cambios');
    } finally {
      setSavingPrendas(false);
    }
  };

  const doSubOrderStatusChange = async (subOrderId, newStatus) => {
    try {
      setChangingSubOrder(subOrderId);
      const updated = await updateSubOrderStatus(id, subOrderId, newStatus);
      setOrder(updated);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error actualizando estado');
    } finally {
      setChangingSubOrder(null);
    }
  };

  const total = Number(order?.pricing?.total_amount || 0);
  const paid = Number(order?.pricing?.paid_amount || 0);
  const balance = Math.max(0, total - paid);
  const CARD_FACTOR = cardSurchargePct > 0 ? (1 + cardSurchargePct / 100) : 1;
  const withSurcharge = (n) => cardSurchargePct > 0 ? Math.round(n * CARD_FACTOR * 100) / 100 : n;
  const isCardPayment = paymentMethod === 'tarjeta' && cardSurchargePct > 0;
  // Primer pago: usar campo de anticipo; pagos subsiguientes: usar balance completo
  const isPrimerPago = paid === 0 && balance > 0;
  // displayPaid: suma real de lo cobrado por método (total - itbis + discount por invoice)
  const displayPaid = (orderInvoices || []).reduce(
    (sum, inv) => sum + (Number(inv.total || 0) - Number(inv.itbis || 0) + Number(inv.discount || 0)), 0
  );
  const displayBalance = isCardPayment ? withSurcharge(balance) : balance;
  const displayTotal   = displayPaid + displayBalance;
  const minAnticipoCash    = total * 0.5;
  // El campo anticipo muestra el monto en la moneda del método seleccionado
  const minAnticipoDisplay = isCardPayment ? withSurcharge(minAnticipoCash) : minAnticipoCash;
  const anticiPoVal        = Math.max(0, parseFloat(anticiPoAmount) || 0);
  const anticiPoError      = isPrimerPago && anticiPoVal < minAnticipoDisplay - 0.009;
  // Convertir a precio efectivo para enviar al POS (POS aplica recargo + ITBIS 18%)
  const anticiPoCash       = isCardPayment && cardSurchargePct > 0
    ? Math.round(anticiPoVal / CARD_FACTOR * 100) / 100
    : anticiPoVal;
  const posAmount          = isPrimerPago ? anticiPoCash : balance;
  const services = order?.services || [];
  const servicesTotal = Number(
    order?.services_total ||
      services.reduce((s, item) => s + Number(item.subtotal || 0), 0),
  );
  const invoiceHistory =
    (order?.invoices || []).length > 0 ? order.invoices : payments;
  const paymentStatus = order?.pricing?.payment_status;
  const isPaid = paymentStatus === "PAGADO";
  const hasBalance = balance > 0;

  const doStatus = async () => {
    try {
      if (!statusToSet) return;

      if (statusToSet === "CANCELADO") {
        setCancelReason("");
        setCancelOpen(true);
        return;
      }

      if (statusToSet === "ENTREGADO" && balance > 0.01) {
        setDeliverWithDebtOpen(true);
        return;
      }

      const updated = await updateTailoringOrderStatus(id, statusToSet);
      setOrder(updated);
      toast.success("Estatus actualizado");
      if (statusToSet === "ENTREGADO") {
        navigate("/tailoring-orders");
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "Error cambiando estatus");
    }
  };

  const confirmCancel = async () => {
    const reason = cancelReason.trim();
    if (!reason) return toast.error("La razón es obligatoria");

    try {
      setCreatingNc(true);
      const result = await cancelTailoringOrder(id, reason);
      setCancelOpen(false);
      setCancelReason("");

      if (result?.auto_credit_note) {
        toast.success(`Pedido cancelado · Nota de crédito ${result.auto_credit_note.credit_note_number} creada`);
      } else {
        toast.success("Pedido cancelado");
      }
      navigate("/tailoring-orders");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Error cancelando pedido");
      setStatusToSet(order?.status || "");
    } finally {
      setCreatingNc(false);
    }
  };

  const openInvoiceModal = () => {
    setInvoiceForm({
      amount: String(balance || ""),
      payment_method: "efectivo",
      reference: "",
    });
    setInvoiceOpen(true);
  };

  const canEdit =
    order?.status !== "ENTREGADO" && order?.status !== "CANCELADO";

  const saveEdits = async () => {
    try {
      const payload = {
        measurements: editMeas,
        changes_requested: editChanges,
        notes: editNotes,
      };
      await updateTailoringOrder(id, payload);
      await load();
      setEditing(false);
      toast.success("Cambios guardados");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Error guardando cambios");
    }
  };

  if (!order) {
    return (
      <div className="text-slate-600">
        {loading ? "Cargando..." : "Pedido no encontrado"}
      </div>
    );
  }

  const handlePrintReceipt = async () => {
    setPrintingReceipt(true);
    try {
      const blob = await downloadTailoringReceipt(id);

      // Si el servidor devolvió un error JSON en vez de PDF, mostrarlo
      if (blob.type === "application/json") {
        const text = await blob.text();
        const json = JSON.parse(text);
        toast.error(json.error || "Error generando comprobante");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      // Usar <a> en vez de window.open para evitar bloqueo del navegador
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 3000);
    } catch (err) {
      console.error("Error generando comprobante:", err);
      toast.error(err.response?.data?.error || err.message || "Error generando comprobante");
    } finally {
      setPrintingReceipt(false);
    }
  };

  const doSendReceipt = async (email) => {
    setSendingEmail(true);
    try {
      await sendTailoringReceipt(id, email);
      toast.success(`Comprobante enviado a ${email}`);
      setSendEmailModal(false);
      setEmailTo("");
    } catch (err) {
      console.error("Error enviando correo:", err);
      toast.error(err.response?.data?.error || err.message || "Error enviando correo. Verifica la configuración SMTP en Ajustes.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendEmail = async () => {
    const email = emailTo.trim();
    if (!email || !email.includes("@")) {
      toast.error("Ingresa un correo válido");
      return;
    }
    const registeredEmail = order?.customer_email || "";
    if (order?.customer_id && email !== registeredEmail) {
      setAskSaveEmail(email);
      return;
    }
    await doSendReceipt(email);
  };

  const confirmSaveEmail = async (doSave) => {
    const email = askSaveEmail;
    setAskSaveEmail(null);
    if (doSave && order?.customer_id) {
      try {
        await updateCustomer(order.customer_id, { email });
        toast.success("Correo guardado en el perfil del cliente");
      } catch (e) { console.error(e); }
    }
    await doSendReceipt(email);
  };

  return (
    <>
    <div className="p-4 space-y-6  max-w-7xl mx-auto" data-testid="tailoring-detail-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Pedido: {order.code ? `${order.code}` : `#${order.id}`}
          </h1>

          <p className="text-slate-600">
            Cliente: <span className="font-medium">{order.customer_name}</span>
            {isNewFormat
              ? <> · <span className="font-medium">{subOrders.length} prenda{subOrders.length !== 1 ? 's' : ''}</span></>
              : <> · Producto: <span className="font-medium">{order?.product_ref?.name}</span></>
            }
            {" "}· Vendedor: <span className="font-medium">{order?.seller_name}</span>
          </p>
        </div>

     
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Resumen</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <div className="text-slate-500 text-sm">Estatus</div>
            <div className="mt-1">
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLORS[order.status] || "bg-slate-100 text-slate-600"}`}>
                {statusLabel(order.status)}
              </span>
            </div>
            {order?.status === "CANCELADO" && (
              <div className="mt-2 text-sm text-red-700">
                <span className="font-medium">Motivo:</span>{" "}
                {order?.cancel_reason || "-"}
              </div>
            )}
          </div>
          <div>
            <div className="text-slate-500 text-sm">Total</div>
            <div className="font-medium">{money(displayTotal)}</div>
            {isPrimerPago && isCardPayment && (
              <div className="text-xs text-slate-400">{money(total)} efectivo</div>
            )}
          </div>
          <div>
            <div className="text-slate-500 text-sm">Pagado</div>
            <div className="font-medium">
              {money(displayPaid)} ({order?.pricing?.payment_status})
            </div>
          </div>
          <div>
            <div className="text-slate-500 text-sm">Pendiente</div>
            <div className="font-medium">{money(displayBalance)}</div>
            {isCardPayment && balance > 0 && (
              <div className="text-xs text-slate-400">{money(balance)} efectivo</div>
            )}
          </div>

          <div className="md:col-span-4">
            <div className="text-slate-500 text-sm">Entrega</div>
            <div className="font-medium">
              {order?.delivery?.due_date
                ? new Date(order.delivery.due_date).toLocaleDateString("es-DO")
                : "-"}
            </div>
          </div>

          {/* Selector de método de pago */}
          {cardSurchargePct > 0 && !isPaid && (
            <div className="md:col-span-4">
              <div className="text-slate-500 text-sm mb-1.5">Método de pago</div>
              <div className="flex gap-2">
                {['efectivo', 'tarjeta'].map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      if (m === paymentMethod) return;
                      // Convertir el anticipo al nuevo método
                      if (isPrimerPago && anticiPoVal > 0 && cardSurchargePct > 0) {
                        if (m === 'tarjeta') {
                          setAnticiPoAmount(withSurcharge(anticiPoVal).toFixed(2));
                        } else {
                          setAnticiPoAmount((anticiPoVal / CARD_FACTOR).toFixed(2));
                        }
                      }
                      setPaymentMethod(m);
                    }}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      paymentMethod === m
                        ? m === 'tarjeta'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                    }`}
                  >
                    {m === 'efectivo' ? '💵 Efectivo' : '💳 Tarjeta'}
                  </button>
                ))}
              </div>
              {isCardPayment && (
                <p className="mt-1.5 text-xs text-slate-400">
             
                  ℹ️ Pago en <strong className="text-slate-700">EFECTIVO</strong> incluye un {cardSurchargePct}% de descuento · Pago con <strong className="text-slate-700">Tarjeta</strong> se le agrega el <strong className="text-slate-700">ITBIS</strong>.
                </p>
                
              )}
            </div>
          )}

  <div className="md:col-span-4 space-y-3 mt-2">
    {/* Campo de anticipo: solo cuando es el primer pago */}
    {!isPaid && isPrimerPago && (
      <div className="flex flex-col gap-1">
        <label className="text-sm text-slate-500">
          Anticipo a cobrar <span className="text-slate-400">(mín. {money(minAnticipoDisplay)})</span>
        </label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={minAnticipoDisplay.toFixed(2)}
            max={displayTotal.toFixed(2)}
            step="0.01"
            value={anticiPoAmount}
            onChange={e => setAnticiPoAmount(e.target.value)}
            className={`w-44 ${anticiPoError ? 'border-red-400 focus:ring-red-300' : ''}`}
          />
          <button
            type="button"
            onClick={() => setAnticiPoAmount(displayTotal.toFixed(2))}
            className="text-xs text-slate-400 hover:text-slate-600 underline"
          >
            Pago completo
          </button>
        </div>
        {anticiPoError && (
          <p className="text-xs text-red-500">El anticipo mínimo es {money(minAnticipoDisplay)} (50%)</p>
        )}
      </div>
    )}
    <div className="flex flex-row items-center justify-center gap-3">
    {!isPaid && order?.status !== 'CANCELADO' && (
      <Button
        onClick={() => {
          if (!cajaOpen && cajaStale) { toast.warning(`Cierra el turno del ${cajaStaleDate} antes de facturar`); return; }
          if (!cajaOpen) { toast.warning('Abre un turno de caja para facturar'); return; }
          if (isPrimerPago && anticiPoError) { toast.warning(`El anticipo mínimo es ${money(minAnticipoDisplay)}`); return; }
          navigate(`/pos?tailoring_order_id=${order.id}&initial_payment=${posAmount.toFixed(2)}&payment_method=${paymentMethod}`);
        }}
        title={cajaOpen ? "Facturar Pedido" : cajaStale ? `Turno del ${cajaStaleDate} sin cerrar` : "Caja cerrada"}
        className={`w-44 ${!cajaOpen ? 'bg-slate-400' : hasBalance ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
      >
        {cajaOpen ? '💰 Facturar' : '🔒 Facturar'}
      </Button>
    )}
    {/* Imprimir y Enviar solo visibles cuando se ha pagado al menos el 50% */}
    {(total > 0 && paid >= total * 0.5) && (
      <>
        <Button
          variant="outline"
          onClick={handlePrintReceipt}
          disabled={printingReceipt}
          title="Imprimir Comprobante"
          className="flex items-center gap-2 w-44"
        >
          <Printer className="w-4 h-4" />
          {printingReceipt ? "Generando..." : "Imprimir"}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
        setEmailTo(order?.customer_email || "");
        setSendEmailModal(true);
      }}
          title="Enviar por Correo"
          className="flex items-center gap-2 w-44"
        >
          <Mail className="w-4 h-4" />
          Enviar
        </Button>
      </>
    )}
  </div>
  </div>
        </CardContent>
      </Card>

      {/* ── Prendas: nuevo formato ── */}
      {isNewFormat ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg">Prendas ({subOrders.length})</CardTitle>
              <div className="flex items-center gap-3 flex-wrap">
                {cardSurchargePct > 0 && (
                  <p className="text-xs text-slate-500">
                    ℹ️ Pago en <strong className="text-slate-700">EFECTIVO</strong> incluye un {cardSurchargePct}% de descuento · Pago con <strong className="text-slate-700">Tarjeta</strong> se le agrega el <strong className="text-slate-700">ITBIS</strong>
                  </p>
                )}
                {['PENDIENTE_PAGO', 'PRE_PRODUCCION'].includes(order?.status) && (
                  <Button size="sm" variant="outline" onClick={openEditPrendas}>
                    ✏️ Editar prendas
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {subOrders.map((so, idx) => {
              const soStatus = so.status || 'PENDIENTE';
              const soStatusColors = {
                PENDIENTE:  'bg-slate-100 text-slate-600 border-slate-200',
                EN_PROCESO: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                LISTO:      'bg-green-100 text-green-700 border-green-200',
              };
              const soNextStatus = { PENDIENTE: 'EN_PROCESO', EN_PROCESO: 'LISTO', LISTO: null };
              const soNextLabel  = { PENDIENTE: '→ En proceso', EN_PROCESO: '→ Listo' };
              const nextSt       = soNextStatus[soStatus];
              const isUpdating   = changingSubOrder === so.id;
              const isFinal      = order?.status === 'ENTREGADO' || order?.status === 'CANCELADO';
              const soSubtotal   = Number(so.subtotal || 0);

              return (
                <div key={so.id || idx} className="border border-slate-200 rounded-lg overflow-hidden">
                  {/* Header prenda */}
                  <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <span className="bg-cyan-100 text-cyan-800 font-bold text-xs rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
                        {so.letter || String.fromCharCode(65 + idx)}
                      </span>
                      <span className="font-semibold text-sm text-slate-900">{so.product_name || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${soStatusColors[soStatus] || ''}`}>
                        {soStatus === 'PENDIENTE' ? 'Pendiente' : soStatus === 'EN_PROCESO' ? 'En proceso' : 'Listo'}
                      </span>
                      {nextSt && !isFinal && (
                        <button type="button"
                          onClick={() => doSubOrderStatusChange(so.id, nextSt)}
                          disabled={isUpdating}
                          className="text-xs px-2 py-0.5 border border-slate-300 rounded hover:bg-cyan-50 hover:border-cyan-400 hover:text-cyan-700 transition-colors disabled:opacity-50">
                          {isUpdating ? '...' : soNextLabel[soStatus]}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Body prenda */}
                  <div className="px-4 py-3 space-y-3 text-sm">
                    {/* Precios */}
                    <div className="space-y-1">
                      {Number(so.item_price) > 0 && (
                        <div className="flex justify-between text-slate-700">
                          <span>• {so.product_name} <span className="text-xs text-slate-400">(confección)</span></span>
                          <div className="text-right">
                            <div className="font-medium">{money(so.item_price)} <span className="text-xs font-normal text-green-600">efectivo</span></div>
                            {cardSurchargePct > 0 && (
                              <div className="text-xs text-slate-400">{money(withSurcharge(Number(so.item_price)))} <span className="text-blue-500">tarjeta</span></div>
                            )}
                          </div>
                        </div>
                      )}
                      {(so.services || []).map(s => (
                        <div key={s.product_id} className="flex justify-between text-slate-700">
                          <span>• {s.name} ×{s.quantity}</span>
                          <div className="text-right">
                            <div className="font-medium">{money(s.subtotal)} <span className="text-xs font-normal text-green-600">ef.</span></div>
                            {cardSurchargePct > 0 && (
                              <div className="text-xs text-slate-400">{money(withSurcharge(Number(s.subtotal)))} <span className="text-blue-500">tarjeta</span></div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between pt-1.5 border-t border-slate-200">
                        <span className="font-semibold text-slate-800">Subtotal</span>
                        <div className="text-right">
                          <div className="font-semibold text-slate-800">
                            {money(soSubtotal)} <span className="text-xs font-normal text-green-600">efectivo</span>
                          </div>
                          {cardSurchargePct > 0 && (
                            <div className="text-xs text-slate-500">
                              {money(withSurcharge(soSubtotal))}{' '}
                              <span className="text-blue-500">tarjeta</span>{' '}
                           
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Medidas */}
                    {so.measurements && Object.values(so.measurements).some(v => v) && (
                      <div>
                        <p className="text-xs text-slate-400 mb-1">Medidas (cm)</p>
                        <div className="grid grid-cols-5 gap-1.5">
                          {['pecho','cintura','cadera','largo','manga'].map(k => (
                            <div key={k} className="border rounded p-1.5 bg-white text-center">
                              <div className="text-xs text-slate-400 capitalize">{k}</div>
                              <div className="font-medium text-sm">{so.measurements[k] || '—'}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cambios solicitados */}
                    {so.changes_requested && (
                      <div className="bg-slate-50 rounded p-2 border border-slate-100">
                        <p className="text-xs text-slate-400 mb-0.5">Cambios solicitados</p>
                        <p className="text-slate-700">{so.changes_requested}</p>
                      </div>
                    )}

                    {/* Instrucciones taller */}
                    {so.work_notes && (
                      <p className="text-xs text-slate-500 italic border-t border-slate-100 pt-2">
                        "{so.work_notes}"
                      </p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Resumen total del pedido */}
            {cardSurchargePct > 0 && (
              <div className="mt-1 border-t-2 border-slate-200 pt-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-center">
                    <div className="text-xs text-green-600 font-medium mb-0.5">💵 Efectivo</div>
                    <div className="font-bold text-green-700 text-base">{money(total)}</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-center">
                    <div className="text-xs text-blue-600 font-medium mb-0.5">💳 Tarjeta </div>
                    <div className="font-bold text-blue-700 text-base">{money(withSurcharge(total))}</div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ── Formato legacy: Servicios + Confección ── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Servicios adicionales</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-600">
                      <th className="py-2 pr-4">Servicio</th>
                      <th className="py-2 pr-4">Cantidad</th>
                      <th className="py-2 pr-4">Precio</th>
                      <th className="py-2 pr-4">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((s) => (
                      <tr key={s.product_id} className="border-b">
                        <td className="py-2 pr-4">{s.name}</td>
                        <td className="py-2 pr-4">{s.quantity}</td>
                        <td className="py-2 pr-4">{money(s.unit_price)}</td>
                        <td className="py-2 pr-4">{money(s.subtotal)}</td>
                      </tr>
                    ))}
                    {services.length === 0 && (
                      <tr><td colSpan={4} className="py-6 text-center text-slate-500">No hay servicios adicionales.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-sm text-slate-600">
                Total servicios: <span className="font-medium">{money(servicesTotal)}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Confección / Cambios</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-end">
                <div className="flex gap-2">
                  {!editing && (
                    <Button variant="outline" onClick={() => setEditing(true)} disabled={!canEdit}>Editar</Button>
                  )}
                  {editing && (
                    <>
                      <Button onClick={saveEdits}>Guardar</Button>
                      <Button variant="outline" onClick={() => {
                        setEditing(false);
                        setEditMeas(order?.customization?.measurements || {});
                        setEditChanges(order?.customization?.changes_requested || "");
                        setEditNotes(order?.notes || "");
                      }}>Cancelar</Button>
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                {Object.entries(editing ? editMeas || {} : order?.customization?.measurements || {}).map(([k, v]) => (
                  <div key={k} className="border rounded p-2 bg-white">
                    <div className="text-slate-500 capitalize">{k}</div>
                    {!editing ? (
                      <div className="font-medium">{v}</div>
                    ) : (
                      <Input value={String(v ?? "")}
                        onChange={(e) => setEditMeas(prev => ({ ...prev, [k]: e.target.value }))}
                        placeholder="0" />
                    )}
                  </div>
                ))}
              </div>

              <div className="border rounded p-3 bg-white">
                <div className="text-slate-500 text-sm">Cambios requeridos</div>
                {!editing ? (
                  <div className="text-slate-800">{order?.customization?.changes_requested || "-"}</div>
                ) : (
                  <Input value={editChanges} onChange={(e) => setEditChanges(e.target.value)} placeholder="Describe los cambios..." />
                )}
              </div>

              <div className="border rounded p-3 bg-white">
                <div className="text-slate-500 text-sm">Notas</div>
                {!editing ? (
                  <div className="text-slate-800">{order?.notes || "-"}</div>
                ) : (
                  <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Observaciones..." />
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ── Estatus y acciones (siempre visible) ── */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Estatus y acciones</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Si no pagó 50%, solo puede cancelar */}
              {paid < total * 0.5 && order?.status === "PENDIENTE_PAGO" && (
                <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                  ⚠️ Se requiere al menos 50% de pago para cambiar de estado. Solo se permite cancelar.
                </p>
              )}
              <Select value={statusToSet} onValueChange={setStatusToSet}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Cambiar estatus" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.filter((s) => {
                    // Si no pagó 50% solo se puede cancelar (o mantener estado actual)
                    const has50 = total > 0 && paid >= total * 0.5;
                    if (!has50 && s.value !== "CANCELADO" && s.value !== order?.status) return false;
                    // No mostrar el estado actual como opción de cambio (excepto PRE_PRODUCCION que es auto)
                    if (s.value === "PRE_PRODUCCION" && order?.status !== "PRE_PRODUCCION") return false;
                    return true;
                  }).map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={doStatus}
                disabled={order?.status === "CANCELADO"}
              >
                Actualizar
              </Button>
            </div>

            {/* Email modal */}
            <Dialog open={sendEmailModal} onOpenChange={setSendEmailModal}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Enviar comprobante por correo</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  {order?.customer_email && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-800">
                      Correo registrado: <strong>{order.customer_email}</strong>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>
                      {order?.customer_email ? "Enviar a otro correo (opcional)" : "Correo electrónico del cliente"}
                    </Label>
                    <Input
                      type="email"
                      placeholder={order?.customer_email || "cliente@ejemplo.com"}
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSendEmail()}
                      autoFocus={!order?.customer_email}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setSendEmailModal(false)}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSendEmail}
                    disabled={sendingEmail}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {sendingEmail ? "Enviando..." : "Enviar comprobante"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Modal: ¿Guardar correo en perfil? */}
            <Dialog open={!!askSaveEmail} onOpenChange={() => setAskSaveEmail(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>¿Guardar correo en el perfil?</DialogTitle>
                </DialogHeader>
                <div className="py-2">
                  <p className="text-sm text-slate-600">
                    ¿Deseas guardar <strong>{askSaveEmail}</strong> como correo de{" "}
                    <strong>{order?.customer_name}</strong> para futuros envíos?
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => confirmSaveEmail(false)} disabled={sendingEmail}>
                    No, solo enviar
                  </Button>
                  <Button onClick={() => confirmSaveEmail(true)} disabled={sendingEmail}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white">
                    {sendingEmail ? "Enviando..." : "Guardar y enviar"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cancelar pedido</DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                  <div className="text-slate-600 text-sm">
                    Indica el motivo. Esta acción bloquea modificaciones y abonos.
                  </div>

                  {paid > 0.01 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                      Este pedido tiene <strong>RD${paid.toLocaleString('es-DO', { minimumFractionDigits: 2 })}</strong> cobrado.
                      Se emitirá automáticamente una nota de crédito al cancelar.
                    </div>
                  )}

                  <div className="space-y-1">
                    <Label>Razón</Label>
                    <Input
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Ej: Cliente canceló / error en medidas / etc."
                    />
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCancelOpen(false);
                        setStatusToSet(order?.status || "");
                      }}
                    >
                      Volver
                    </Button>
                    <Button variant="destructive" onClick={confirmCancel} disabled={creatingNc}>
                      {creatingNc ? 'Procesando...' : paid > 0.01 ? 'Cancelar y emitir NC' : 'Confirmar cancelación'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

     

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Historial de pagos </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-600">
                  <th className="py-2 pr-4">Fecha</th>
                  <th className="py-2 pr-4">Factura</th>
                  <th className="py-2 pr-4">Método</th>
                  <th className="py-2 pr-4">Monto</th>
                </tr>
              </thead>
              <tbody>
                {orderInvoices.map((inv) => {
                  const methodLabels = { efectivo: 'Efectivo', tarjeta: 'Tarjeta', transferencia: 'Transferencia', credito: 'Crédito' };
                  const methodText = inv.payment_methods?.length > 0
                    ? inv.payment_methods.map(pm => methodLabels[pm.method] || pm.method).join(' + ')
                    : methodLabels[inv.payment_method] || inv.payment_method || '-';
                  return (
                  <tr key={inv.id} className="border-b">
                    <td className="py-2 pr-4">
                      {inv.created_at ? new Date(inv.created_at).toLocaleString("es-DO") : ""}
                    </td>
                    <td className="py-2 pr-4">
                      {inv.invoice_number || inv.code || inv.id}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">{methodText}</td>
                    <td className="py-2 pr-4">
                      {money((inv.total || inv.amount_paid || 0) - (inv.itbis || 0) + (inv.discount || 0))}
                    </td>
                  </tr>
                  );
                })}

                {orderInvoices.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-slate-500">
                      No hay facturas registradas para este pedido.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>

      {/* ── Diálogo edición de prendas ── */}
      <Dialog open={showEditPrendas} onOpenChange={setShowEditPrendas}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar prendas del pedido</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {editPrendas.map((so, pIdx) => (
              <div key={pIdx} className="border border-slate-200 rounded-xl overflow-hidden">
                {/* Header prenda */}
                <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200">
                  <span className="font-semibold text-sm text-slate-700">Prenda {pIdx + 1}{so.product_name ? ` — ${so.product_name}` : ''}</span>
                  {editPrendas.length > 1 && (
                    <button onClick={() => setEditPrendas(p => p.filter((_, i) => i !== pIdx))}
                      className="text-xs text-red-500 hover:text-red-700 font-medium">
                      🗑 Eliminar
                    </button>
                  )}
                </div>

                <div className="p-4 space-y-3">
                  {/* Producto + Precio */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Producto</Label>
                      <Select value={so.product_id} onValueChange={v => {
                        const prod = allProducts.find(p => p.id === v);
                        updatePrenda(pIdx, 'product_id', v);
                        updatePrenda(pIdx, 'product_name', prod?.name || '');
                        if (prod?.price && !so.item_price) updatePrenda(pIdx, 'item_price', String(prod.price));
                      }}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {allProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Precio confección (RD$)</Label>
                      <Input className="h-8 text-sm" type="number" min="0" value={so.item_price}
                        onChange={e => updatePrenda(pIdx, 'item_price', e.target.value)} />
                    </div>
                  </div>

                  {/* Descuento */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Descuento (%)</Label>
                      <Input className="h-8 text-sm" type="number" min="0" max="100" placeholder="0"
                        value={so.discount_pct} onChange={e => updatePrenda(pIdx, 'discount_pct', e.target.value)} />
                    </div>
                  </div>

                  {/* Medidas */}
                  <div className="space-y-1">
                    <Label className="text-xs">Medidas (cm)</Label>
                    <div className="grid grid-cols-5 gap-1.5">
                      {MEDIDAS.map(k => (
                        <div key={k}>
                          <p className="text-xs text-slate-400 capitalize text-center mb-0.5">{k}</p>
                          <Input className="h-7 text-xs text-center" type="number" min="0"
                            value={so.measurements[k] || ''}
                            onChange={e => updatePrendaMedida(pIdx, k, e.target.value)} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cambios / Notas */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Cambios solicitados</Label>
                      <textarea rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        value={so.changes_requested} onChange={e => updatePrenda(pIdx, 'changes_requested', e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Instrucciones taller</Label>
                      <textarea rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        value={so.work_notes} onChange={e => updatePrenda(pIdx, 'work_notes', e.target.value)} />
                    </div>
                  </div>

                  {/* Servicios */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Servicios adicionales</Label>
                    {so.services.map((s, sIdx) => (
                      <div key={sIdx} className="flex items-center gap-2">
                        <Select value={s.product_id} onValueChange={v => {
                          const prod = allProducts.find(p => p.id === v);
                          updatePrendaService(pIdx, sIdx, 'product_id', v);
                          updatePrendaService(pIdx, sIdx, 'name', prod?.name || '');
                          if (prod?.price) updatePrendaService(pIdx, sIdx, 'unit_price', String(prod.price));
                        }}>
                          <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Servicio..." /></SelectTrigger>
                          <SelectContent>
                            {allProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input className="h-8 text-xs w-16" type="number" min="1" placeholder="Cant."
                          value={s.quantity} onChange={e => updatePrendaService(pIdx, sIdx, 'quantity', e.target.value)} />
                        <Input className="h-8 text-xs w-24" type="number" min="0" placeholder="Precio"
                          value={s.unit_price} onChange={e => updatePrendaService(pIdx, sIdx, 'unit_price', e.target.value)} />
                        <button onClick={() => removePrendaService(pIdx, sIdx)}
                          className="text-red-400 hover:text-red-600 text-xs">✕</button>
                      </div>
                    ))}
                    <button onClick={() => addPrendaService(pIdx)}
                      className="text-xs text-cyan-600 hover:text-cyan-800 font-medium">
                      + Agregar servicio
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Agregar prenda */}
            <button
              onClick={() => setEditPrendas(p => [...p, { ...emptyPrenda, measurements: { pecho: '', cintura: '', cadera: '', largo: '', manga: '' } }])}
              className="w-full border-2 border-dashed border-slate-300 rounded-xl py-3 text-sm text-slate-500 hover:border-cyan-400 hover:text-cyan-600 transition-colors">
              + Agregar prenda
            </button>
          </div>

          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" className="flex-1" onClick={() => setShowEditPrendas(false)}>Cancelar</Button>
            <Button className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white" onClick={saveEditPrendas} disabled={savingPrendas}>
              {savingPrendas ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: marcar ENTREGADO con balance pendiente */}
      <Dialog open={deliverWithDebtOpen} onOpenChange={setDeliverWithDebtOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Balance pendiente</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-slate-700">
              Este pedido tiene un balance pendiente de{' '}
              <span className="font-bold text-red-600">{money(balance)}</span>.
              El pedido será marcado como <strong>ENTREGADO</strong>.
            </p>
            <p className="text-sm text-slate-500">
              La comisión se generará cuando el balance quede en cero.
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDeliverWithDebtOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="outline"
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
              onClick={async () => {
                setDeliverWithDebtOpen(false);
                try {
                  const updated = await updateTailoringOrderStatus(id, 'ENTREGADO');
                  setOrder(updated);
                  navigate(`/pos?tailoring_order_id=${id}`);
                } catch (e) {
                  toast.error(e?.response?.data?.error || 'Error cambiando estatus');
                }
              }}
            >
              Ir a cobrar
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={async () => {
                setDeliverWithDebtOpen(false);
                try {
                  const updated = await updateTailoringOrderStatus(id, 'ENTREGADO');
                  setOrder(updated);
                  toast.success('Estatus actualizado');
                  navigate('/tailoring-orders');
                } catch (e) {
                  toast.error(e?.response?.data?.error || 'Error cambiando estatus');
                }
              }}
            >
              Entregar sin cobrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
};

export default TailoringOrderDetail;